import { useEffect, useMemo, useRef, useState } from "react";
import "./Paywall.scss";
import {
  faCalendarCheck,
  faCheck,
  faComments,
  faCrown,
  faHandHoldingDollar,
  faUsersViewfinder,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getSubscriptionCheckoutSession } from "services/SauceService";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getBusinessSubscription } from "redux-context/business";
import { SubscriptionType } from "enums/SubscriptionType";

export enum PaywallSource {
  Creators = "creators",
  CreatorInvite = "creator-invite",
  LaunchCampaign = "launch-campaign",
  LaunchCollab = "launch-collab",
  SendMessage = "send-message",
  BookingActions = "booking-actions",
  UpgradePlan = "upgrade-plan",
}

export type PaywallOptions = {
  source?: PaywallSource;
  onSubscribe?: () => void;
  priceId?: string;
};

type Props = {
  open: boolean;
  onRequestClose: () => void;
  options?: PaywallOptions;
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const labelsBySource: Record<string, string> = {
  [PaywallSource.Creators]: "Upgrade to see all creators",
  [PaywallSource.CreatorInvite]: "Subscribe & Send Invite",
  [PaywallSource.LaunchCampaign]: "Subscribe & Launch Campaign",
  [PaywallSource.LaunchCollab]: "Subscribe & Launch Collab",
  [PaywallSource.SendMessage]: "Subscribe & Send Message",
  [PaywallSource.BookingActions]: "Subscribe & Continue",
  [PaywallSource.UpgradePlan]: "Upgrade Plan",
};

const Paywall: React.FC<Props> = ({ open, onRequestClose, options }) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>();
  const { subscription, business } = useSelector(
    (state: BusinessState) => state.business,
  );
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;

  const [paymentComplete, setPaymentComplete] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const pollIntervalRef = useRef<number | null>(null);

  const onCompletePayment = useMemo(() => {
    return () => {
      // immediately refresh subscription
      dispatch(getBusinessSubscription());

      // clear any existing interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // start polling every 2 seconds
      pollIntervalRef.current = window.setInterval(() => {
        dispatch(getBusinessSubscription());
      }, 2000);
      setIsPolling(true);
    };
  }, [dispatch]);

  useEffect(() => {
    // clear polling when modal is closed
    if (!open) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setPaymentComplete(false);
      setIsPolling(false);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onRequestClose();
    }

    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onRequestClose]);

  function onBackdropClick(e: React.MouseEvent) {
    if (e.target === modalRef.current) onRequestClose();
  }

  const onSubscribeClick = async () => {
    if (loading) return;
    setLoading(true);
    const params = {
      businessId: business?._id,
    };
    try {
      const resp: any = await getSubscriptionCheckoutSession(params);

      const secret = resp?.sessionId;
      if (!secret)
        throw new Error("Missing client secret from server response");

      setClientSecret(secret);
      // clear previous interval if any
      // if ((window as any)._paywallSubscriptionInterval) {
      //   clearInterval((window as any)._paywallSubscriptionInterval);
      //   (window as any)._paywallSubscriptionInterval = null;
      // }

      // (window as any)._paywallSubscriptionInterval = window.setInterval(() => {
      //   dispatch(getBusinessSubscription());
      // }, 15000);
    } catch (err: any) {
      console.error(err);
      window.alert(
        err?.message || "Unable to start checkout. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!freemium) {
      options?.onSubscribe?.();
      onRequestClose();
    }
  }, [freemium, onRequestClose, options]);

  return (
    <>
      <div
        ref={modalRef}
        className={`pw-backdrop ${open ? "pw-visible" : ""}`}
        onClick={onBackdropClick}
        aria-hidden={!open}
      >
        <div
          id="modalContent"
          className={`pw-modal ${open ? "pw-open" : ""} ${clientSecret ? "pw-modal--embedded" : ""}`}
          role="dialog"
          aria-modal={open}
          aria-label="Paywall modal"
        >
          {paymentComplete ? (
            <div className="pw-success">
              <div className="pw-success-icon" aria-hidden>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h2>Payment Successful!</h2>
              <p>Thank you for your subscription.</p>
              {isPolling && <div className="pw-spinner" aria-hidden />}
              <button className="pw-cta" type="button" onClick={onRequestClose}>
                Close
              </button>
            </div>
          ) : clientSecret ? (
            <div style={{ width: "100%", height: "100%", overflow: "scroll" }}>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret,
                  onComplete: onCompletePayment,
                }}
              >
                <div className="pw-embedded">
                  <button
                    className="pw-close"
                    onClick={() => {
                      // unmount embedded checkout if user closes modal
                      setClientSecret(null);
                      onRequestClose();
                    }}
                    aria-label="Close"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                  </button>
                  <EmbeddedCheckout />
                </div>
              </EmbeddedCheckoutProvider>
            </div>
          ) : (
            <>
              <div className="pw-left">
                <div className="pw-decor pw-decor--orange" />
                <div className="pw-decor pw-decor--blue" />

                <div className="pw-left-inner">
                  <div className="pw-badge">
                    <FontAwesomeIcon icon={faCrown} />
                    <span>Recommended Plan</span>
                  </div>

                  <h2 className="pw-title">Talk of Town</h2>
                  <p className="pw-sub">Self Service Plan</p>
                  <p className="pw-note"></p>

                  <div className="pw-price">
                    <span className="pw-amount">$100</span>
                    <span className="pw-period">/mo</span>
                  </div>

                  <div className="pw-features">
                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        Unlimited applications
                      </span>
                    </div>

                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        Unlimited locations
                      </span>
                    </div>

                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        $25 booking fee{" "}
                        <small>(only when application accepted)</small>
                      </span>
                    </div>
                  </div>

                  <button
                    className={`pw-cta ${loading ? "pw-cta--loading" : ""}`}
                    type="button"
                    onClick={onSubscribeClick}
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading
                      ? "Processing…"
                      : labelsBySource[options?.source || ""] ||
                        "Subscribe Now"}
                  </button>

                  <p className="pw-small">Cancel anytime, no commitment.</p>
                  <p className="pw-terms">
                    By subscribing, you agree to{" "}
                    <a href="https://www.mustard.love/terms-of-service">
                      Mustard&apos;s Terms of Service
                    </a>
                  </p>
                </div>
              </div>

              <div className="pw-right">
                <button
                  className="pw-close"
                  onClick={onRequestClose}
                  aria-label="Close"
                  type="button"
                >
                  <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>

                <h3 className="pw-right-title">Unlock Full Access</h3>
                <p className="pw-right-sub">
                  Upgrade to the Talk of Town plan and get everything you need
                  to scale your influencer marketing.
                </p>

                <div className="pw-right-scroll">
                  <h4 className="pw-list-title">
                    Everything included in this plan
                  </h4>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--orange">
                      <FontAwesomeIcon
                        icon={faUsersViewfinder}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Unlimited Reach</h5>
                      <p>
                        Invite unlimited influencers and get guaranteed 3+
                        applications each month.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--blue">
                      <FontAwesomeIcon
                        icon={faWandMagicSparkles}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Smart Matching</h5>
                      <p>
                        Use AI to match influencers or handpick from
                        applications — your choice.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--purple">
                      <FontAwesomeIcon
                        icon={faCalendarCheck}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Easy Scheduling</h5>
                      <p>
                        Easily schedule influencers to visit as often as you
                        want.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--green">
                      <FontAwesomeIcon
                        icon={faHandHoldingDollar}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Flexible Compensation</h5>
                      <p>
                        Provide flexible compensation options, including food,
                        cash, or affiliate commissions.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--pink">
                      <FontAwesomeIcon icon={faComments} aria-hidden="true" />
                    </div>
                    <div className="pw-item-body">
                      <h5>Direct Access</h5>
                      <p>
                        Gain access to the largest local influencer pool,
                        complete with performance insights and direct chat.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Paywall;
